import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight, AlignLeft, Music2, Copy, Check, Play, Pause, Maximize2, Minimize2 } from 'lucide-react';
import { ChordProParser, HtmlDivFormatter } from 'chordsheetjs';
import { postProcessChordHtml, transposeKey, detectAccidental, stripToLyrics } from '../utils/chordpro';
import { getStoredCapo, setStoredCapo, parseCapoDirective, CAPO_MIN, CAPO_MAX } from '../utils/capo';

/** CSS applied to the chord-sheet HTML emitted by ChordSheetJS's HtmlDivFormatter. */
const CHORD_SHEET_CSS = `
  .csj-wrap .paragraph { margin-bottom: 1.5rem; }
  .csj-wrap .row { display: flex; flex-wrap: wrap; line-height: 1; }
  .csj-wrap .column { display: inline-flex; flex-direction: column; }
  .csj-wrap .chord {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.75rem;
    font-weight: 700;
    color: #16a34a;
    line-height: 1.3;
    min-height: 1.3em;
    white-space: pre;
    padding-right: 0.4em;
  }
  /* No chord on this syllable in a MIXED row — keep invisible space so
     lyrics below still align with adjacent chord-bearing columns */
  .csj-wrap .chord:empty { visibility: hidden; }
  /* Lyric-only rows (post-processed class) — collapse chord space entirely */
  .csj-wrap .row.row--lyric-only .chord { display: none; min-height: 0; }
  /* Chord-only rows (post-processed class) — collapse lyrics space entirely */
  .csj-wrap .row.row--chord-only .lyrics { display: none; min-height: 0; line-height: 0; }
  .csj-wrap .lyrics {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.875rem;
    line-height: 1.45;
    color: #1e293b;
    white-space: pre-wrap;
    word-break: break-word;
    overflow-wrap: break-word;
    padding-right: 0.4em;
  }
  .csj-wrap .comment {
    display: inline-block;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #16a34a;
    background: rgba(22,163,74,0.1);
    border-radius: 0.25rem;
    padding: 0.1em 0.5em;
    margin-top: 0.25rem;
    margin-bottom: 0.125rem;
  }
  @media (min-width: 640px) {
    .csj-wrap .chord  { font-size: 1rem; }
    .csj-wrap .lyrics { font-size: 1.125rem; }
  }
  @media (min-width: 1024px) {
    .csj-wrap .chord  { font-size: 1.1rem; }
    .csj-wrap .lyrics { font-size: 1.25rem; }
  }
  /* Two-column "fit to screen" layout on desktop — verses stay intact across columns */
  @media (min-width: 768px) {
    .csj-wrap {
      column-count: 2;
      column-gap: 2.5rem;
      column-fill: balance;
    }
    .csj-wrap .paragraph {
      break-inside: avoid;
      page-break-inside: avoid;
    }
  }
  /* Dark mode */
  .dark .csj-wrap .chord   { color: #4ade80; }
  .dark .csj-wrap .lyrics  { color: #f1f5f9; }
  .dark .csj-wrap .comment { color: #4ade80; background: rgba(74,222,128,0.1); }
  /* Lyrics-only mode: hide all chords and chord-only rows */
  .csj-wrap.csj-lyrics-mode .chord { display: none !important; min-height: 0 !important; }
  .csj-wrap.csj-lyrics-mode .row.row--chord-only { display: none !important; }
  .csj-wrap.csj-lyrics-mode .column { flex-direction: row; }
  /* Merge syllables split by chord positions by removing inter-column spacing */
  .csj-wrap.csj-lyrics-mode .column .lyrics { padding-right: 0; }
  .csj-wrap.csj-lyrics-mode .column + .column .lyrics::before { content: ''; }
`;

const _parser = new ChordProParser({ normalizeChords: false });
const _formatter = new HtmlDivFormatter({ normalizeChords: false });

/**
 * Full-screen song viewer with swipe navigation.
 * Props:
 *   songs          — flat array of song meta objects [{song_id, title, artist, key, ...}]
 *   contents       — { [song_id]: string } — loaded content cache
 *   loadingContent — { [song_id]: bool }
 *   initialIndex   — which song to show first
 *   onClose        — callback to close the modal
 *   onFetchContent — (song_id) => void — request content load
 */
const SongViewerModal = ({ songs, contents, loadingContent, initialIndex, onClose, onFetchContent, hideNav = false, initialSemitoneOffset = 0, initialAccidental = null }) => {
  const [index, setIndex] = useState(initialIndex ?? 0);
  const [lyricsOnly, setLyricsOnly] = useState(false);
  const [semitoneOffset, setSemitoneOffset] = useState(initialSemitoneOffset);
  // Accidental: initialise from prop (if provided) or auto-detect from first song's key
  const [accidental, setAccidental] = useState(() =>
    initialAccidental ?? detectAccidental(songs[initialIndex ?? 0]?.key)
  );
  // Capo — personal, device-local preference (never synced to other users).
  // Falls back to the song's {capo:} ChordPro directive once content loads.
  const [capo, setCapo] = useState(() => getStoredCapo(songs[initialIndex ?? 0]?.song_id) ?? 0);
  const capoEditedRef = useRef(false); // true once the user manually changes capo for the current song
  const [copied, setCopied] = useState(false);
  const [navVisible, setNavVisible] = useState(true);
  // Fit-to-screen mode (desktop/tablet only) — scales chord sheet to fit viewport without scrolling.
  // Persisted across modal mounts via localStorage.
  const [fitToScreen, setFitToScreen] = useState(() => {
    try { return localStorage.getItem('csj-fit-to-screen') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('csj-fit-to-screen', fitToScreen ? '1' : '0'); } catch { /* ignore */ }
  }, [fitToScreen]);
  // Metronome state
  const [metronomeOn, setMetronomeOn] = useState(false);
  const [beatIndex, setBeatIndex] = useState(-1);
  const metronomeOnRef = useRef(false); // stable ref for reading metronomeOn inside effects
  const audioCtxRef = useRef(null);
  const nextNoteTimeRef = useRef(0.0);
  const currentBeatRef = useRef(0);
  const schedulerTimerRef = useRef(null);
  const beatsPerMeasureRef = useRef(4); // updated by beatsPerMeasure useMemo
  const isCompoundRef = useRef(false);  // true for 6/8, 9/8, 12/8 etc.
  const contentRef = useRef(null);
  const containerRef = useRef(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const navTimerRef = useRef(null);
  const wheelCooldownRef = useRef(false);
  // Detect touch-only devices — on these we hide the nav bar entirely (swipe to navigate).
  // Desktop browsers (even at narrow widths) support hover, so we keep the overlay for them.
  const isTouchDevice = useRef(typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0));
  // Tracks whether this is the very first render of this mount cycle.
  // Reset in cleanup so React 18 Strict Mode's double-invoke also skips correctly.
  const isFirstRender = useRef(true);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const suppressNextPopstate = useRef(false);

  // Back-button support — opener calls pushState before mounting us.
  // We just listen for popstate and close, suppressing it when the user
  // closes via the X button or Escape (those call handleClose which pops manually).
  useEffect(() => {
    const handlePopstate = () => {
      if (suppressNextPopstate.current) {
        suppressNextPopstate.current = false;
        return;
      }
      onCloseRef.current(semitoneOffset, accidental);
    };
    window.addEventListener('popstate', handlePopstate);
    return () => window.removeEventListener('popstate', handlePopstate);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Use for all UI-triggered closes (X button, Escape, prev/next when at boundary).
  // Suppresses the popstate that history.back() fires, then calls onClose directly.
  const handleClose = useCallback(() => {
    suppressNextPopstate.current = true;
    window.history.back();
    onCloseRef.current(semitoneOffset, accidental);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [semitoneOffset, accidental]);

  const song = songs[index] || null;
  const rawContent = song ? (contents[song.song_id] || '') : '';
  const isLoading = song ? !!loadingContent[song.song_id] : false;

  // Parse + format with ChordSheetJS (memoised per rawContent + target key + semitone offset + accidental)
  const targetKey = song?.key || null;
  const displayKey = useMemo(
    () => transposeKey(targetKey || '', semitoneOffset, accidental),
    [targetKey, semitoneOffset, accidental]
  );
  const fileCapo = useMemo(() => parseCapoDirective(rawContent), [rawContent]);
  const chartHtml = useMemo(() => {
    if (!rawContent) return '';
    try {
      const META_RE = /^\{(title|subtitle|artist|composer|lyricist|album|key|capo|tempo|time|duration|year|ccli|copyright|flow|sorttitle|sortartist|url|google_doc_key)[^}]*\}\s*\n?/gim;
      // Extract the original key from the raw ChordPro content BEFORE stripping,
      // because META_RE removes {key:} and parsed.key would be null after cleaning.
      const fileKeyMatch = rawContent.match(/^\{key\s*:\s*([^}]+)\}/im);
      const fileKey = fileKeyMatch ? fileKeyMatch[1].trim() : null;

      const cleaned = rawContent.replace(META_RE, '');
      let parsed = _parser.parse(cleaned);

      // Transpose from the file's original key to the setlist/target key.
      // Uses parsed.transpose(delta) because parsed.key is null after meta-stripping.
      if (targetKey && fileKey && fileKey !== targetKey) {
        const SEMI = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'];
        const FLAT = { Db:1, Eb:3, Gb:6, Ab:8, Bb:10, Cb:11, Fb:4 };
        const toIdx = k => {
          const r = k?.match(/^([A-G][b#]?)/)?.[1];
          if (!r) return -1;
          const i = SEMI.indexOf(r);
          return i !== -1 ? i : (FLAT[r] ?? -1);
        };
        const fromIdx = toIdx(fileKey);
        const toIdxVal = toIdx(targetKey);
        if (fromIdx !== -1 && toIdxVal !== -1) {
          const delta = ((toIdxVal - fromIdx) + 12) % 12;
          if (delta !== 0) parsed = parsed.transpose(delta);
        }
      }
      // Apply manual semitone adjustment (view-only, not saved)
      if (semitoneOffset !== 0) {
        parsed = parsed.transpose(semitoneOffset);
      }
      // Capo — shifts the displayed chord SHAPES down; the sounding key is unchanged
      if (capo) {
        parsed = parsed.transpose(-capo);
      }
      // Accidental conversion is applied in postProcessChordHtml chord-by-chord
      // (bypasses Song.useAccidental's bass-note bug with slash chords)
      return postProcessChordHtml(_formatter.format(parsed), accidental);
    } catch {
      return '';
    }
  }, [rawContent, targetKey, semitoneOffset, accidental, capo]);

  // Parse time signature from {time: N/D} ChordPro directive; fall back to 4/4
  // Compound meters (6/8, 9/8, 12/8): denominator=8 and numerator divisible by 3 →
  //   secondary accent clicks on beats 3, 6, 9 … (middle-pitch between downbeat and regular)
  const { beatsPerMeasure, timeSig, isCompound } = useMemo(() => {
    const match = rawContent.match(/^\{time\s*:\s*(\d+)\s*\/\s*(\d+)\s*\}/im);
    const beats = match ? parseInt(match[1], 10) : 4;
    const denom = match ? parseInt(match[2], 10) : 4;
    const sig = match ? `${match[1]}/${match[2]}` : '4/4';
    const compound = denom === 8 && beats % 3 === 0;
    beatsPerMeasureRef.current = beats;
    isCompoundRef.current = compound;
    return { beatsPerMeasure: beats, timeSig: sig, isCompound: compound };
  }, [rawContent]);

  // Prefetch current + adjacent songs
  useEffect(() => {
    const toFetch = [index - 1, index, index + 1].filter(i => i >= 0 && i < songs.length);
    toFetch.forEach(i => {
      const s = songs[i];
      if (s && !contents[s.song_id] && !loadingContent[s.song_id]) {
        onFetchContent(s.song_id);
      }
    });
  }, [index, songs]); // eslint-disable-line

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') goNext();
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') goPrev();
      else if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [index]); // eslint-disable-line

  // Lock body scroll while modal is open to prevent double-scrollbar
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Scroll to top + reset key offset/accidental when song changes.
  // Skip on initial mount (including React 18 Strict Mode's double-invoke) so
  // the initialSemitoneOffset/initialAccidental props are not overwritten right after mount.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const wasPlaying = metronomeOnRef.current;
    if (contentRef.current) contentRef.current.scrollTop = 0;
    setSemitoneOffset(0);
    setAccidental(detectAccidental(songs[index]?.key));
    capoEditedRef.current = false;
    setCapo(getStoredCapo(songs[index]?.song_id) ?? 0);
    stopMetronome();
    // Auto-start metronome for next song if it was already running and new song has a BPM
    if (wasPlaying && songs[index]?.bpm) {
      setTimeout(startMetronome, 150);
    }
  }, [index]); // eslint-disable-line

  const goNext = useCallback(() => setIndex(i => Math.min(i + 1, songs.length - 1)), [songs.length]);
  const goPrev = useCallback(() => setIndex(i => Math.max(i - 1, 0)), []);

  // Once content loads, default capo from the file's {capo:} directive —
  // but only if the user hasn't already set a preference (stored or manual edit this session).
  useEffect(() => {
    if (!song) return;
    if (capoEditedRef.current) return;
    if (getStoredCapo(song.song_id) !== null) return;
    if (fileCapo !== null) setCapo(fileCapo);
  }, [fileCapo]); // eslint-disable-line

  const stepCapo = useCallback((delta) => {
    setCapo(c => {
      const next = Math.max(CAPO_MIN, Math.min(CAPO_MAX, c + delta));
      capoEditedRef.current = true;
      if (song) setStoredCapo(song.song_id, next);
      return next;
    });
  }, [song]);

  // ── Metronome ────────────────────────────────────────────────────────────
  const stopMetronome = useCallback(() => {
    clearTimeout(schedulerTimerRef.current);
    schedulerTimerRef.current = null;
    // Keep AudioContext alive — closing it requires a new user gesture to reopen.
    // Just stop the scheduler and reset state; AudioContext is reused on next start.
    setMetronomeOn(false);
    setBeatIndex(-1);
  }, []);

  const startMetronome = useCallback(() => {
    const bpmVal = Number(song?.bpm) || 0;
    if (!bpmVal) return;
    // Reuse existing AudioContext if still open (avoids needing a new user gesture).
    // Only create a new one if none exists or the previous was closed.
    let ctx = audioCtxRef.current;
    if (!ctx || ctx.state === 'closed') {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
    }
    if (ctx.state === 'suspended') ctx.resume();
    nextNoteTimeRef.current = ctx.currentTime + 0.05;
    currentBeatRef.current = 0;
    setMetronomeOn(true);

    const secondsPerBeat = 60.0 / bpmVal;
    const SCHEDULE_AHEAD = 0.1; // seconds to schedule ahead

    const tick = () => {
      if (!audioCtxRef.current) return; // already stopped
      while (nextNoteTimeRef.current < audioCtxRef.current.currentTime + SCHEDULE_AHEAD) {
        const beat = currentBeatRef.current;
        const t = nextNoteTimeRef.current;

        // Synthesized click: beat 0 (downbeat) = high, compound secondary accent = medium, rest = low
        const osc = audioCtxRef.current.createOscillator();
        const gain = audioCtxRef.current.createGain();
        osc.connect(gain);
        gain.connect(audioCtxRef.current.destination);
        const isSecondaryAccent = isCompoundRef.current && beat > 0 && beat % 3 === 0;
        osc.frequency.value = beat === 0 ? 880 : isSecondaryAccent ? 660 : 440;
        gain.gain.setValueAtTime(1.0, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
        osc.start(t);
        osc.stop(t + 0.06);

        // Schedule visual beat indicator update
        const delay = Math.max(0, (t - audioCtxRef.current.currentTime) * 1000);
        const b = beat;
        setTimeout(() => setBeatIndex(b), delay);

        nextNoteTimeRef.current += secondsPerBeat;
        currentBeatRef.current = (currentBeatRef.current + 1) % beatsPerMeasureRef.current;
      }
      schedulerTimerRef.current = setTimeout(tick, 25);
    };
    tick();
  }, [song?.bpm]); // eslint-disable-line

  const toggleMetronome = useCallback(() => {
    if (metronomeOn) stopMetronome();
    else startMetronome();
  }, [metronomeOn, stopMetronome, startMetronome]);

  // Keep stable ref in sync so index-change effect can read it without stale closure
  useEffect(() => { metronomeOnRef.current = metronomeOn; }, [metronomeOn]);

  // Cleanup metronome on unmount; also reset isFirstRender for React 18 Strict Mode re-mount
  useEffect(() => {
    return () => {
      isFirstRender.current = true; // allow second Strict Mode mount to also skip
      clearTimeout(schedulerTimerRef.current);
      if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});
    };
  }, []); // eslint-disable-line

  // Fit-to-screen is a desktop/tablet-only feature (toggle hidden < 768 px).
  // If the viewport narrows below md while fit is on, force it off so the
  // chord sheet returns to its natural size and the user isn't stranded
  // without a way to disable it.
  useEffect(() => {
    const onResize = () => {
      if (typeof window !== 'undefined' && window.innerWidth < 768 && fitToScreen) {
        setFitToScreen(false);
      }
    };
    onResize(); // run once on mount in case opened on mobile with stored preference = on
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [fitToScreen]);

  // Stop metronome when switching to Lyrics mode (control is hidden but audio would keep running)
  useEffect(() => {
    if (lyricsOnly) stopMetronome();
  }, [lyricsOnly]); // eslint-disable-line

  // Fit-to-screen — measure rendered content and apply a scale transform so the
  // chord sheet fills the viewport. Bidirectional: shrinks long songs to avoid
  // scrolling, enlarges short songs to use available space (capped at 2.5x).
  const applyFitToScreen = useCallback(() => {
    const container = contentRef.current;
    const wrap = container?.querySelector('.csj-wrap');
    if (!container || !wrap) return;
    // Always reset transform first so subsequent measurements reflect natural size.
    wrap.style.transform = '';
    wrap.style.transformOrigin = '';
    wrap.style.width = '';
    wrap.style.marginBottom = '';
    if (!fitToScreen) return;
    // Force a layout flush before measuring.
    void wrap.offsetWidth;
    // Subtract the container's padding so we measure the true space available
    // for the wrap (not including the px-5 py-6 padding).
    const cs = getComputedStyle(container);
    const pt = parseFloat(cs.paddingTop) || 0;
    const pb = parseFloat(cs.paddingBottom) || 0;
    const pl = parseFloat(cs.paddingLeft) || 0;
    const pr = parseFloat(cs.paddingRight) || 0;
    const aw = container.clientWidth - pl - pr;
    const ah = container.clientHeight - pt - pb;
    const nw = wrap.scrollWidth;
    const nh = wrap.scrollHeight;
    if (aw <= 0 || ah <= 0 || nw <= 0 || nh <= 0) return;
    let scale = Math.min(aw / nw, ah / nh);
    // Only scale DOWN to fit. Column-count layout already fills width by default,
    // so enlarging would just create horizontal overflow without giving meaningful
    // gain in readability.
    if (scale > 1) scale = 1;
    if (scale > 0 && scale < 0.999) {
      wrap.style.transformOrigin = 'top left';
      wrap.style.transform = `scale(${scale})`;
      wrap.style.width = `${100 / scale}%`;
      // Prevent the scaled-away whitespace from producing a scrollbar.
      wrap.style.marginBottom = `${-(nh * (1 - scale))}px`;
    }
  }, [fitToScreen]);

  useLayoutEffect(() => {
    applyFitToScreen();
  }, [applyFitToScreen, chartHtml, lyricsOnly, index, semitoneOffset, accidental]);

  // Re-run on window resize / orientation change while fit-to-screen is active.
  useEffect(() => {
    if (!fitToScreen) return;
    window.addEventListener('resize', applyFitToScreen);
    return () => window.removeEventListener('resize', applyFitToScreen);
  }, [fitToScreen, applyFitToScreen]);

  // Auto-hide nav after 3 s of inactivity; resets on mouse move
  const resetNavTimer = useCallback(() => {
    setNavVisible(true);
    clearTimeout(navTimerRef.current);
    navTimerRef.current = setTimeout(() => setNavVisible(false), 3000);
  }, []);

  // Start the initial hide timer on mount, clear on unmount
  useEffect(() => {
    if (!isTouchDevice.current) resetNavTimer();
    return () => clearTimeout(navTimerRef.current);
  }, []); // eslint-disable-line

  const handleCopy = useCallback(async () => {
    const text = lyricsOnly ? stripToLyrics(rawContent) : rawContent;
    try {
      await navigator.clipboard.writeText(text || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  }, [lyricsOnly, rawContent]);

  const handleTouchStart = (e) => {
    e.stopPropagation(); // prevent ServiceDetail tab-swipe handler from stealing the gesture
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    e.stopPropagation(); // prevent ServiceDetail from applying swipeDelta transform
  };

  const handleTouchEnd = (e) => {
    e.stopPropagation(); // prevent ServiceDetail from navigating to another tab
    if (touchStartX.current === null) return;
    const dx = touchStartX.current - e.changedTouches[0].clientX;
    const dy = touchStartY.current - e.changedTouches[0].clientY;
    // Only trigger for horizontal swipes (dx dominant)
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 48) {
      if (dx > 0) goNext();
      else goPrev();
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  // MacBook trackpad two-finger horizontal swipe — wheel events with dominant deltaX.
  // Attach as non-passive so we can preventDefault and prevent the page from scrolling
  // sideways. A 600 ms cooldown prevents momentum-scroll from skipping multiple songs.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return; // vertical scroll — ignore
      if (Math.abs(e.deltaX) < 30) return;                  // dead zone — ignore nudges
      if (wheelCooldownRef.current) return;                  // still in cooldown
      e.preventDefault();
      wheelCooldownRef.current = true;
      setTimeout(() => { wheelCooldownRef.current = false; }, 600);
      if (e.deltaX > 0) goNext(); else goPrev();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [goNext, goPrev]);

  if (!song) return null;

  return (
    <>
      {/* Scoped CSS for ChordSheetJS output — outside flex container to avoid layout impact */}
      <style>{CHORD_SHEET_CSS}</style>
      <div
        ref={containerRef}
        className="fixed inset-0 z-[60] bg-white dark:bg-slate-900 flex flex-col overflow-hidden"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseMove={!isTouchDevice.current ? resetNavTimer : undefined}
        data-testid="song-viewer-modal"
      >
      {/* Top bar — two-row layout so title is never obscured by controls */}
      <div className="shrink-0 border-b border-slate-200 dark:border-slate-700/60 bg-white/95 dark:bg-slate-900/95">
        {/* Row 1: [X] [Title/Artist] [Chords toggle + Fit-to-screen + Metronome] */}
        <div className="flex items-start gap-3 px-4 pt-3 pb-2">
          <button
            onClick={() => handleClose()}
            className="mt-0.5 shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            data-testid="viewer-close-btn"
            aria-label="Close viewer"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Left column: title → artist */}
          <div className="flex-1 min-w-0">
            <p className="text-slate-900 dark:text-white font-semibold text-sm leading-tight break-words" data-testid="viewer-song-title">
              {song.title}
            </p>
            {song.artist && (
              <p className="text-slate-500 dark:text-slate-400 text-xs truncate">{song.artist}</p>
            )}
          </div>

          {/* Right column: Chords/Lyrics + Fit-to-screen + Metronome — single horizontal row */}
          <div className="shrink-0 flex items-center gap-1.5">
            <div className="flex items-center rounded-lg overflow-hidden border border-slate-200 dark:border-slate-600 divide-x divide-slate-200 dark:divide-slate-600">
              <button
                onClick={() => setLyricsOnly(v => !v)}
                data-testid="viewer-lyrics-toggle"
                className={`flex items-center gap-1.5 px-3 h-8 text-xs font-medium transition-all ${
                  lyricsOnly
                    ? 'bg-green-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                }`}
              >
                {lyricsOnly ? <AlignLeft className="w-3.5 h-3.5" /> : <Music2 className="w-3.5 h-3.5" />}
                {lyricsOnly ? 'Lyrics' : 'Chords'}
              </button>
            </div>
            {/* Fit-to-screen toggle — desktop & tablet only (md and up) */}
            <button
              onClick={() => setFitToScreen(v => !v)}
              data-testid="viewer-fit-toggle"
              aria-label={fitToScreen ? 'Exit fit to screen' : 'Fit content to screen'}
              title={fitToScreen ? 'Showing fit to screen — click to disable' : 'Fit content to screen'}
              className={`hidden md:flex items-center justify-center w-8 h-8 rounded-lg border text-xs transition-all ${
                fitToScreen
                  ? 'bg-green-600 text-white border-green-600 hover:bg-green-700'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:bg-slate-200 dark:hover:bg-slate-600'
              }`}
            >
              {fitToScreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
            </button>
            {/* Metronome — inline, square button matching the others, with BPM label beside it */}
            {song?.bpm && !lyricsOnly && (
              <>
                <button
                  onClick={toggleMetronome}
                  data-testid="metronome-toggle-btn"
                  aria-label={metronomeOn ? 'Stop metronome' : 'Start metronome'}
                  title={metronomeOn ? 'Stop metronome' : `Start metronome (${timeSig} · ${song.bpm} BPM)`}
                  className={`relative flex items-center justify-center w-8 h-8 flex-shrink-0 rounded-lg text-white transition-colors ${
                    metronomeOn
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-slate-400 dark:bg-slate-600 hover:bg-slate-500'
                  }`}
                >
                  {metronomeOn && beatIndex !== -1 && (
                    <span
                      key={beatIndex}
                      className={`absolute inset-0 rounded-lg animate-ping ${
                        beatIndex === 0 ? 'bg-green-300' : 'bg-green-400'
                      }`}
                      style={{ animationDuration: '0.4s', animationIterationCount: 1 }}
                    />
                  )}
                  {metronomeOn
                    ? <Pause className="w-3.5 h-3.5 relative z-10" />
                    : <Play className="w-3.5 h-3.5 relative z-10" />
                  }
                </button>
                <span className="text-[10px] font-mono text-slate-400 dark:text-slate-500 leading-tight tabular-nums whitespace-nowrap" data-testid="metronome-bpm">
                  {timeSig} · {song.bpm}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Row 2: Key / Accidental / Capo controls — full-width row, never squeezed by the right-side buttons */}
        {!lyricsOnly && (
          <div className="flex items-center gap-1 px-4 pb-3 flex-wrap">
            {song.key && (
              <div className="flex items-center gap-0.5" data-testid="viewer-key-control">
                <button
                  onClick={() => setSemitoneOffset(n => n - 1)}
                  data-testid="viewer-key-down"
                  className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 text-sm font-bold transition-colors"
                  aria-label="Transpose down"
                >−</button>
                <button
                  onClick={() => semitoneOffset !== 0 && setSemitoneOffset(0)}
                  data-testid="viewer-current-key"
                  title={semitoneOffset !== 0 ? `Original: ${targetKey} — click to reset` : targetKey}
                  className={`px-2 py-0.5 rounded text-xs font-mono font-semibold min-w-[2.5rem] text-center transition-colors ${
                    semitoneOffset !== 0
                      ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 cursor-pointer hover:bg-green-200'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 cursor-default'
                  }`}
                >
                  {displayKey || targetKey}
                </button>
                <button
                  onClick={() => setSemitoneOffset(n => n + 1)}
                  data-testid="viewer-key-up"
                  className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 text-sm font-bold transition-colors"
                  aria-label="Transpose up"
                >+</button>
              </div>
            )}
            {/* Sharp / Flat toggle — always shown in chords mode */}
            <button
              onClick={() => setAccidental(a => a === 'b' ? '#' : 'b')}
              data-testid="viewer-accidental-toggle"
              title={accidental === 'b' ? 'Currently: flats — click for sharps' : 'Currently: sharps — click for flats'}
              className={`w-6 h-6 flex items-center justify-center rounded text-xs font-bold transition-colors bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600 ${song.key ? 'ml-1' : ''}`}
            >
              {accidental === 'b' ? '♭' : '♯'}
            </button>
            {/* Capo — personal, device-only preference. Shifts displayed chord shapes; sounding key is unaffected. */}
            <div className="flex items-center gap-0.5 ml-1.5 pl-1.5 border-l border-slate-200 dark:border-slate-700" data-testid="viewer-capo-control">
              <span className="text-[10px] font-semibold text-slate-400 uppercase mr-0.5">Capo</span>
              <button
                onClick={() => stepCapo(-1)}
                disabled={capo <= CAPO_MIN}
                data-testid="viewer-capo-down"
                className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 text-sm font-bold transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Decrease capo"
              >−</button>
              <span
                data-testid="viewer-capo-value"
                title={capo > 0 ? `Capo on fret ${capo} — shapes shown, sounding key unchanged` : 'No capo'}
                className={`px-2 py-0.5 rounded text-xs font-mono font-semibold min-w-[1.5rem] text-center ${
                  capo > 0
                    ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200'
                }`}
              >
                {capo}
              </span>
              <button
                onClick={() => stepCapo(1)}
                disabled={capo >= CAPO_MAX}
                data-testid="viewer-capo-up"
                className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 text-sm font-bold transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Increase capo"
              >+</button>
            </div>
          </div>
        )}
      </div>

      {/* Content — single scrollable area, no horizontal overflow */}
      <div
        ref={contentRef}
        className="flex-1 overflow-y-auto overflow-x-hidden px-5 py-6 select-text"
        data-testid="viewer-content"
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-full gap-3 text-slate-400">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-500" />
            <span className="text-sm">Loading…</span>
          </div>
        ) : !rawContent ? (
          <p className="text-slate-400 dark:text-slate-500 text-sm italic text-center mt-12">No content available for this song.</p>
        ) : lyricsOnly ? (
          <div
            className="csj-wrap csj-lyrics-mode"
            dangerouslySetInnerHTML={{ __html: chartHtml }}
          />
        ) : (
          <div
            className="csj-wrap"
            dangerouslySetInnerHTML={{ __html: chartHtml }}
          />
        )}
      </div>

      {/* Bottom navigation — hidden on touch devices (swipe to navigate), auto-hide overlay on desktop */}
      {!hideNav && !isTouchDevice.current && (
        <div className={`flex absolute bottom-0 left-0 right-0 items-center justify-between gap-4 px-4 pr-44 py-4 pt-10 bg-gradient-to-t from-white dark:from-slate-900 to-transparent transition-opacity duration-300 ${navVisible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
          <button
            onClick={goPrev}
            disabled={index === 0}
            data-testid="viewer-prev-btn"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-slate-100/90 dark:bg-slate-800/90 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm backdrop-blur-sm"
          >
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>

          <span className="text-slate-500 dark:text-slate-400 text-sm font-medium tabular-nums" data-testid="viewer-position">
            {index + 1} / {songs.length}
          </span>

          <button
            onClick={goNext}
            disabled={index === songs.length - 1}
            data-testid="viewer-next-btn"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-slate-100/90 dark:bg-slate-800/90 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm backdrop-blur-sm"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
      </div>
    </>
  );
};

export default SongViewerModal;
